/* eslint-disable @typescript-eslint/member-ordering */
import {
    AbstractConnectivityGraph,
    AirwiresValidator,
    AnyPcbBakedNode,
    AnyPcbNode,
    CommandType,
    DocumentService,
    DrcEnabledCheckers,
    DrcManager,
    FeatureFlagValues,
    getFirstTopLevelContainerNode,
    IDrcValidator,
    IFillNodesGoods,
    IVirtualNodesGoodsFromRules,
    MissingChildUidError,
    OverlappingCopperValidator,
    PcbBakedNode,
    PcbDocumentState,
    PcbLayoutEngine,
    pcbLayoutRootNodeId,
    PcbNodeHelper,
    PcbNodesMap,
    PcbNodeTypes,
    PcbPrimitiveStore,
    PcbRuleSetsMap,
    PcbTracingGraph,
} from "@buildwithflux/core";
import {Command, getSubTreeUids, IProblem, NodeUid} from "@buildwithflux/models";
import {areWeTestingWithJest, Logger, Records} from "@buildwithflux/shared";
import {Remote} from "comlink";
import {assign, chain, pick, values} from "lodash";

import {workerRunComputeAirwires} from "modules/connectivity/pcbConnectivityGraphWorkerAdapter";
import {DocumentStore} from "modules/document/state/document";
import {workerRunCopperOverlapDrc} from "modules/drc_validator/copperOverlapWorkerAdapter";
import {FeatureFlagsStore} from "modules/feature_flags/FeatureFlagsStore";
import {createPcbLayoutEngineWorker} from "modules/pcb_layout_engine/pcbLayoutEngineInMain";
import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";
import {usePcbEditorUiStore} from "modules/stores/pcb/PcbEditorUiStore";
import {usePcbLayerViewStore} from "modules/stores/pcb/PcbLayerViewStore";
import {IProblemsStore, useProblemsStore} from "modules/stores/pcb/ProblemsStore";

import {createBoundedQueue} from "./createBoundedQueue";

/**
 * This class's service instance should be used from the main thread for baking nodes and storing the results, cleverly
 * called "baked goods". It abstracts away some lower level details:
 *   1. setting nodes in document service
 *   2. setting virtual rules in document service
 *   3. setting shapes in the shapes store (NOTE: upcoming in https://github.com/buildwithflux/flux-app/pull/4601)
 *   4. triggering DRC recalculation with all changes
 */
export class PcbBakedGoodsManager {
    /**
     * Enqueues a DRC processing task and manages the queue. Management involves executing
     * tasks in the queue sequentially as well as limiting the number of tasks that can be
     * enqueued at a given time.
     *
     * The queuing mechanism only allows two tasks to be enqueued at a given time - the
     * task currently being processed, and a subsequent task. This is to ensure that the
     * DRC processing remains more responsive, avoiding intermediary tasks that are
     * redundant when newer tasks are enqueued.
     */
    private boundedQueueDrc = createBoundedQueue<void[]>();

    private documentServiceCommandBatch: Command[] = [];

    /**
     * When true, incremental baking operations (bakeNodesAndUpdate, bakeNodesAndAdd,
     * removeNodesAndUpdateFills, reload) will short-circuit and return immediately.
     * This is used on mobile to suppress baking until the user explicitly opts in
     * via the "Load PCB Anyway" button, at which point firstLoad() is called and
     * this flag is set to false.
     */
    private suppressBaking = false;

    constructor(
        private readonly documentService: DocumentService,
        private readonly pcbPrimitiveStore: PcbPrimitiveStore,
        private readonly pcbConnectivityGraph: AbstractConnectivityGraph,
        private readonly pcbTracingGraph: PcbTracingGraph,
        private readonly featureFlagsStore: FeatureFlagsStore,
        protected readonly logger: Logger,
        private readonly documentStore: DocumentStore,
    ) {}

    /**
     * Set whether baking operations should be suppressed.
     * When true, incremental baking operations will short-circuit and return immediately.
     */
    public setSuppressBaking(value: boolean) {
        this.suppressBaking = value;
    }

    /**
     * Returns whether baking operations are currently suppressed.
     */
    public isBakingSuppressed(): boolean {
        return this.suppressBaking;
    }

    public async reload(
        documentState: PcbDocumentState,
        treeNodes: PcbNodesMap<AnyPcbNode>,
        pcbLayoutRules: PcbRuleSetsMap,
    ) {
        if (this.suppressBaking) {
            return {drcCompletionPromise: Promise.resolve()};
        }
        return await this.bakeAllAndAdd(documentState, treeNodes, pcbLayoutRules, true);
    }

    /**
     * To be called when loading a document for the first time. It also inits
     * the PcbLayerViewStore which depends on the active layout node stackup.
     */
    public async firstLoad(
        documentState: PcbDocumentState,
        treeNodes: PcbNodesMap<AnyPcbNode>,
        pcbLayoutRules: PcbRuleSetsMap,
    ) {
        // NOTE: top level layout could be deleted
        const activeNode = getFirstTopLevelContainerNode(documentState.pcbLayoutNodes || {});

        usePcbLayerViewStore
            .getState()
            .initLayerVisibilityMaps(documentState.layerViewConfig, activeNode, documentState.uid);

        await this.bakeAllAndAdd(documentState, treeNodes, pcbLayoutRules, false);

        if (activeNode) {
            const activeBakedNode = this.getAllNodes()[activeNode.uid] as
                | PcbBakedNode<PcbNodeTypes.layout>
                | PcbBakedNode<PcbNodeTypes.footprint>;
            usePcbLayerViewStore.getState().updateLayerVisibilityMap(activeBakedNode);
        }
        usePcbEditorUiStore.getState().setInitialPcbStateReady(true);
    }

    /**
     * Clear all nodes in documentService and reset suppressBaking state
     */
    public clear() {
        this.suppressBaking = false;
        this.documentServiceCommandBatch.push({
            type: CommandType.clearDocumentCommand,
        });
        this.pcbPrimitiveStore.clear();
        this.pcbConnectivityGraph.clear();
        this.pcbTracingGraph.clear();
    }

    public async removeNodesAndUpdateFills(
        documentState: PcbDocumentState,
        pcbLayoutNodes: PcbNodesMap<AnyPcbNode>,
        pcbLayoutRuleSets: PcbRuleSetsMap,
        nodesToRemove: AnyPcbNode[],
    ) {
        if (this.suppressBaking) {
            return {drcCompletionPromise: Promise.resolve()};
        }
        this.documentService.setDataAsStale(true);

        // Initialize the DRC recalculation
        // DRC is finally recalculated at the end of this method once all changes have been collected
        const drc = this.createDrcManager();

        const pcbLayoutEngine = await this.getPcbLayoutEngine(documentState, pcbLayoutNodes, pcbLayoutRuleSets);

        await this.removeNodes(nodesToRemove, drc, pcbLayoutEngine);

        // It is important for perceived perf to execute an extra `processCommmandBatch` here, immediately
        // after removing the nodes above and before fills are executed below. This is because in large projects
        // such as `support/induct-acu-v2-rev1-3k4g` fills can take a long time (eg 25s) to run, and waiting
        // for that before removing the node from the display makes it feel like a remove action did nothing.
        // For (some but not all) other common actions we have other solutions/workarounds, eg for adding
        // traces, we have projected segments, and adding components is normally done on schematic, so the
        // action has visible effect, so we only do this extra step when we remove nodes.
        this.processCommandBatch();

        {
            const virtualFillNodes = await pcbLayoutEngine.generateFillNodes();

            // NOTE: update shapesMap before updating any baked nodes, this is important for downstream
            // modules such as DRC. See diagram and description in PR: https://github.com/buildwithflux/flux-app/pull/8734
            this.setShapesMap(virtualFillNodes);

            /* Finally handle virtualFillNodes */ //
            await this.updateVirtualNodes(virtualFillNodes, drc, pcbLayoutEngine);
        }

        // TODO: put this in a finally block or something - if an exception is thrown that prevents us getting to this point
        // then subsequent updates will attempt to process stale commands
        this.processCommandBatch();

        this.documentService.setDataAsStale(false);

        const drcCompletionPromise = this.boundedQueueDrc.enqueue(drc.recalculate());
        return {drcCompletionPromise};
    }

    /**
     * Pass information from redux, bake rules for the node and set the baked node in document service
     *
     * @param documentState - document state from redux
     * @param pcbLayoutNodes - pcbLayoutNodes from redux
     * @param pcbLayoutRuleSets - pcbLayoutRuleSets from redux
     * @param nodes - the nodes we will update and bake
     * @param {boolean} hasInheritedRules - whether or not this update involves inherited rules
     * @param {boolean} hasInheritedCalculations - whether or not this update involves inherited calculations
     * @param {boolean} requiresDescendantRebake - whether or not this update requires descendant rebake
     * @param {boolean} requiresNetDescendantRebake - whether or not this update requires net descendant rebake
     * @param removeNodes - any nodes we will remove before baking
     */
    public async bakeNodesAndUpdate(
        documentState: PcbDocumentState,
        pcbLayoutNodes: PcbNodesMap<AnyPcbNode>,
        pcbLayoutRuleSets: PcbRuleSetsMap,
        nodes: AnyPcbNode[],
        hasInheritedRules: boolean,
        hasInheritedCalculations: boolean,
        requiresDescendantRebake: boolean,
        requiresNetDescendantRebake: boolean,
        removeNodes?: AnyPcbNode[],
    ) {
        if (this.suppressBaking) {
            return {drcCompletionPromise: Promise.resolve()};
        }
        this.documentService.setDataAsStale(true);

        // Initialize the DRC recalculation
        // DRC is finally recalculated at the end of this method once all changes have been collected
        const drc = this.createDrcManager();

        let resetFillsCache = false;

        const pcbLayoutEngine = await this.getPcbLayoutEngine(documentState, pcbLayoutNodes, pcbLayoutRuleSets);

        if (removeNodes) {
            await this.removeNodes(removeNodes, drc, pcbLayoutEngine);
            resetFillsCache = true;
        }

        const {virtualNodesGoodsFromRules, pcbLayoutNodes: nodesToUpdate} = await pcbLayoutEngine.rebakeNodes(
            nodes.map((node) => node.uid),
            hasInheritedRules,
            hasInheritedCalculations,
            requiresDescendantRebake,
            requiresNetDescendantRebake,
        );

        /* First, handle virtual nodes/rules from rules */
        if (virtualNodesGoodsFromRules) {
            const didCleanup = Boolean(
                await this.cleanUpVirtualNodes(virtualNodesGoodsFromRules, drc, pcbLayoutEngine),
            );
            resetFillsCache ??= didCleanup;
            this.updateRuleSets(virtualNodesGoodsFromRules, drc);
        }

        resetFillsCache = resetFillsCache || this.shouldResetFillsCache(nodesToUpdate);

        this.updateNodes(values(nodesToUpdate), drc);

        if (Object.values(nodesToUpdate).some((node) => node.type === PcbNodeTypes.polygon)) {
            // A new polygon requires cleaning up the fills cache
            resetFillsCache = true;
        }

        {
            const virtualFillNodes = await pcbLayoutEngine.generateFillNodes(
                resetFillsCache ? undefined : nodesToUpdate,
            );

            // NOTE: update shapesMap before updating any baked nodes, this is important for downstream
            // modules such as DRC. See diagram and description in PR: https://github.com/buildwithflux/flux-app/pull/8734
            this.setShapesMap(virtualFillNodes);

            /* Finally handle virtualFillNodes */ //
            await this.updateVirtualNodes(virtualFillNodes, drc, pcbLayoutEngine);
        }

        // Update smart via configs cache if any smart via nodes are updated
        if (values(nodesToUpdate).some((node) => node.type === PcbNodeTypes.smartVia)) {
            await this.updateSmartViaConfigsCache(pcbLayoutEngine);
        }

        this.processCommandBatch();

        this.documentService.setDataAsStale(false);

        const drcCompletionPromise = this.boundedQueueDrc.enqueue(drc.recalculate());
        return {drcCompletionPromise};
    }

    /**
     * Pass information from redux, bake rules for the node and set the baked node in document service
     *
     * @param documentState - document state from redux
     * @param pcbLayoutNodes - pcbLayoutNodes from redux
     * @param pcbLayoutRuleSets - pcbLayoutRuleSets from redux
     * @param nodes - the nodes we will update and bake
     * @param removeNodes - any nodes we will remove before baking
     */
    public async bakeNodesAndAdd(
        documentState: PcbDocumentState,
        pcbLayoutNodes: PcbNodesMap<AnyPcbNode>,
        pcbLayoutRuleSets: PcbRuleSetsMap,
        nodes: AnyPcbNode[],
        removeNodes?: AnyPcbNode[],
    ) {
        if (this.suppressBaking) {
            return {drcCompletionPromise: Promise.resolve()};
        }
        this.documentService.setDataAsStale(true);

        // Initialize the DRC recalculation
        // DRC is finally recalculated at the end of this method once all changes have been collected
        const drc = this.createDrcManager();

        let resetFillsCache = false;

        const pcbLayoutEngine = await this.getPcbLayoutEngine(documentState, pcbLayoutNodes, pcbLayoutRuleSets);

        if (removeNodes) {
            await this.removeNodes(removeNodes, drc, pcbLayoutEngine);
            resetFillsCache = true;
        }

        // Prevent supplying virtual nodes for baking. They should instead be a product of the baking process
        nodes = nodes.filter((node) => !PcbNodeHelper.isVirtualNode(node));

        const {virtualNodesGoodsFromRules, pcbLayoutNodes: nodesToAdd} = await pcbLayoutEngine.bakeNodes(nodes);

        /* First, handle virtual nodes/rules from rules */
        if (virtualNodesGoodsFromRules) {
            const didCleanup = Boolean(
                await this.cleanUpVirtualNodes(virtualNodesGoodsFromRules, drc, pcbLayoutEngine),
            );
            resetFillsCache ??= didCleanup;
            this.updateRuleSets(virtualNodesGoodsFromRules, drc);
        }

        if (Object.values(nodesToAdd).some((node) => node.type === PcbNodeTypes.polygon)) {
            // A new polygon requires cleaning up the fills cache
            resetFillsCache = true;
        }

        // Now `pcbLayoutNodes` contains the new baked nodes, add them in document service
        const nodesToAddArray = values(nodesToAdd);
        // The problem .. we're getting a PCB re-render mid call to bakNodesAndAdd(..), causing a re-render before primitives exist
        this.addNodes(nodesToAddArray, drc);

        {
            const virtualFillNodes = await pcbLayoutEngine.generateFillNodes(resetFillsCache ? undefined : nodesToAdd);

            // NOTE: update shapesMap before updating any baked nodes, this is important for downstream
            // modules such as DRC. See diagram and description in PR: https://github.com/buildwithflux/flux-app/pull/8734
            this.setShapesMap(virtualFillNodes);

            /* Finally handle virtualFillNodes */ //
            await this.updateVirtualNodes(virtualFillNodes, drc, pcbLayoutEngine);
        }

        // Update smart via configs cache if any smart via nodes are added
        if (values(nodesToAdd).some((node) => node.type === PcbNodeTypes.smartVia)) {
            await this.updateSmartViaConfigsCache(pcbLayoutEngine);
        }

        this.processCommandBatch();

        this.documentService.setDataAsStale(false);

        const drcCompletionPromise = this.boundedQueueDrc.enqueue(drc.recalculate());
        return {drcCompletionPromise};
    }

    /**
     * Dispatches all collected document commands to document service, and then clears the collection of commands.
     * The batch is cleared upfront so that if a command throws, stale commands are not re-processed
     * on a subsequent retry (e.g. via bakeNodesFromLatestPatchWithRetry's reload fallback).
     */
    private processCommandBatch() {
        const commands = this.documentServiceCommandBatch;
        this.documentServiceCommandBatch = [];
        for (const command of commands) {
            this.documentService.applyCommand(command);
        }
    }

    /**
     * There are many scenarios that we need to access all nodes with all
     * information. This means we want to access all virtual + non-virtual
     * nodes, AND each node contains its latest baked rules. Here we are
     * exposing such an API
     *
     * Please note this is different than `pcbLayoutNodes` in redux in two ways:
     * 1. This includes both non-virtual and virtual nodes
     * 2. Each node contains `bakedRules`
     *
     * @returns {PcbNodesMap<AnyPcbBakedNode>} all nodes we have, including both non-virutal and virtual nodes.
     */
    private getAllNodes(): Readonly<PcbNodesMap<AnyPcbBakedNode>> {
        return this.documentService.snapshot().pcbLayoutNodes;
    }

    /**
     * Bakes all nodes (from redux), starting from the top level ones, and traverse the tree
     * in pre-order
     *
     * @param {PcbDocumentState} documentState
     * @param {PcbNodesMap<AnyPcbNode>} treeNodes
     * @param {PcbRuleSetsMap} pcbLayoutRules
     * @param clearEverything
     */
    private async bakeAllAndAdd(
        documentState: PcbDocumentState,
        treeNodes: PcbNodesMap<AnyPcbNode>,
        pcbLayoutRules: PcbRuleSetsMap,
        clearEverything?: boolean,
    ) {
        this.documentService.setDataAsStale(true);

        // Initialize the DRC recalculation
        // DRC is finally recalculated at the end of this method once all changes have been collected
        const drc = this.createDrcManager();

        const pcbLayoutEngine = await this.getPcbLayoutEngine(documentState, treeNodes, pcbLayoutRules);

        const bakedGoods = await pcbLayoutEngine.bakeAllNodes();

        const {pcbLayoutNodes, virtualNodesGoodsFromRules, virtualFillNodes} = bakedGoods;

        if (clearEverything) {
            // ⚠️ This notifies subscribers of `PcbPrimitiveStoreOperation.Clear` event potentially causing re-renders
            // and visible flickers
            this.clear();
        }

        // NOTE: update shapesMap before updating any baked nodes, this is important for downstream
        // modules such as DRC. See diagram and description in PR: https://github.com/buildwithflux/flux-app/pull/8734

        // ⚠️ WARNING: this notifies subscribers of an `UpdateFills` event
        this.setShapesMap(virtualFillNodes);

        const rootNode = pcbLayoutNodes[pcbLayoutRootNodeId];
        if (!rootNode) {
            throw new Error("Root node not found");
        }
        this.addSubTreesToDocumentService(
            [rootNode],
            pcbLayoutNodes,
            drc,
            // Previously we used `pcbLayoutEngine.treeTraveler` property in order to reuse the
            // TreeTraveler cache. However, after moving the `PcbLayoutEngine` to a web worker, the
            // cache now lives inside the worker and we can't access it.
            // Example: 817 PCB nodes take around 3ms to be cached again. At the time of the
            // testing, this was 0.002% of the total time to bake the PCB and add them to the
            // document service.
        );

        if (virtualNodesGoodsFromRules) {
            this.updateRuleSets(virtualNodesGoodsFromRules, drc);
        }

        // ⚠️ TODO: could lead to subtle flickers in canvas
        await this.updateSmartViaConfigsCache(pcbLayoutEngine);

        await this.updateVirtualNodes(virtualFillNodes, drc, pcbLayoutEngine);
        await this.cleanUpVirtualNodes(virtualNodesGoodsFromRules, drc, pcbLayoutEngine);

        this.processCommandBatch();

        this.documentService.setDataAsStale(false);

        const drcCompletionPromise = this.boundedQueueDrc.enqueue(drc.recalculate());
        return {drcCompletionPromise};
    }

    /**
     * Adds a sub tree to document service.
     *
     * This is method is usually useful when
     * 1. First time loading the project
     * 2. Anytime we need to re-load the project, such as updating GlobalRuleSets
     * 3. Adding virtual nodes
     * 4. Back-filling nodes
     *
     * @param {AnyPcbBakedNode[]} subTreeRootNodes - root nodes of the subTree
     * @param {PcbNodesMap<AnyPcbNode>} allNodes - nodes map containing the whole sub tree
     * @return {void}
     */
    private addSubTreesToDocumentService(
        subTreeRootNodes: AnyPcbBakedNode[],
        allNodes: PcbNodesMap<AnyPcbBakedNode>,
        drc: DrcManager,
    ): void {
        const nodesToAdd = [...subTreeRootNodes];
        // Get all descendants recursively
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        subTreeRootNodes.forEach((subTreeRootNode) => {
            for (const nodes of this.yieldSubTreeRecursive(subTreeRootNode, allNodes)) {
                for (const node of nodes) {
                    nodesToAdd.push(node);
                }
            }
        });
        // Finally add the root nodes and their descendants altogether
        this.addNodes(nodesToAdd, drc, true);
    }

    private *yieldSubTreeRecursive(
        subTreeRootNode: AnyPcbBakedNode,
        allNodes: PcbNodesMap<AnyPcbBakedNode>,
    ): Generator<AnyPcbBakedNode[]> {
        const childUids = subTreeRootNode.childUids;

        if (childUids) {
            yield values(pick(allNodes, childUids));
            for (const childUid of childUids) {
                const childNode = allNodes[childUid];
                if (childNode) {
                    yield* this.yieldSubTreeRecursive(childNode, allNodes);
                } else {
                    FluxLogger.captureError(
                        new MissingChildUidError(`node ${subTreeRootNode.uid} with type ${subTreeRootNode.type}`),
                    );
                }
            }
        }
    }

    /**
     * Add one or more nodes to documentService. This method creates a `setPcbNodes` operation
     * and pass new nodes as the operation's payload; then ask documentService to perform this operation
     *
     * @param {AnyPcbBakedNode[]} nodes - the nodes we want to add, they should be baked nodes
     * @param {DrcManager} drc - DRC recalculation object
     * @param {boolean} skipSort - skip topological sort if set. We only skip when reloading the whole board
     */
    private addNodes(nodes: AnyPcbBakedNode[], drc: DrcManager, skipSort?: boolean) {
        // Don't apply commands for empty node lists, to avoid triggering unnecessary notifications
        // to downstream subscribers
        if (!nodes.length) {
            return;
        }

        // Add nodes to documentService
        this.documentServiceCommandBatch.push({
            type: CommandType.enrichedAddPcbNodesCommand,
            nodes,
            skipSort,
        });

        // Add nodes for DRC recalculation
        drc.recordNodesChange("add", nodes);
    }

    /**
     * Remove one or more nodes from document service.  This method creates a `removePcbNodes` operation
     * and pass new nodes as the operation's payload; then ask documentService to perform this operation
     *
     * @param {AnyPcbBakedNode[]} nodes - nodes we want to remove
     * @param {DrcManager} drc - DRC recalculation object
     */
    private async removeNodes(
        nodes: AnyPcbNode[],
        drc: DrcManager,
        pcbLayoutEngine: PcbLayoutEngine | Remote<PcbLayoutEngine>,
    ) {
        // Remove the nodes from the worker's document state/pcb nodes tree
        // While the removal of nodes from the worker's state is already done through `syncPatchesWithWebWorkers()`
        // function called as a redux side effect (see `patchHandlerEpicSideEffects.ts` file), there are instances
        // where this function (`removeNodesAndUpdateFills`) is called outside of the redux flow.
        await pcbLayoutEngine.removeNodes(nodes.map((node) => node.uid));

        // Get child nodes to delete
        nodes = nodes
            .filter(Boolean) // only try to delete nodes that exist
            .map((node) => getSubTreeUids({pcbNodes: this.getAllNodes(), nodeUid: node.uid, includeRoot: true}))
            .flat()
            .map((uid) => this.getAllNodes()[uid]!)
            .filter(Boolean); // only try to delete nodes that exist

        // Don't apply commands for empty node lists, to avoid triggering unnecessary notifications
        // to downstream subscribers
        if (!nodes.length) {
            return;
        }

        // Remove them from documentService
        const nodeUids = nodes.map((node) => node.uid);
        this.documentServiceCommandBatch.push({
            type: CommandType.removePcbNodesCommand,
            nodeUids,
        });

        // And remove any rule sets that have the same ID, which should always be
        // true for generated rules.
        const ruleSetUids = nodeUids.filter((uid) => {
            return this.documentService.snapshot().pcbLayoutRuleSets[uid];
        });
        this.documentServiceCommandBatch.push({
            type: CommandType.removePcbRuleSetsCommand,
            ruleSetUids,
        });

        // Add nodes for DRC recalculation
        drc.recordNodesChange("remove", nodes);
        drc.recordRuleSetsChange("remove", ruleSetUids);
    }

    /**
     * Update one or more nodes in document service.This method creates a `setPcbNodes` operation
     * and pass new nodes as the operation's payload; then ask documentService to perform this operation
     *
     * @param {AnyPcbNode[]} nodes - nodes we want to remove
     * @param {DrcManager} drc - DRC recalculation object
     */
    private updateNodes(nodes: AnyPcbBakedNode[], drc: DrcManager) {
        // Don't apply commands for empty node lists, to avoid triggering unnecessary notifications
        // to downstream subscribers
        if (!nodes.length) {
            return;
        }

        this.documentServiceCommandBatch.push({
            type: CommandType.enrichedUpdatePcbNodesCommand,
            nodes,
        });

        // Add nodes for DRC recalculation
        drc.recordNodesChange("replace", nodes);
    }

    /**
     * This checks for changes in a few baked rules that effectively remove a
     * node from a fill layer and so require a reset of that fill layer's cache.
     *
     * @see fillGetKeepoutMode
     */
    private shouldResetFillsCache(nodesToUpdate: PcbNodesMap<AnyPcbBakedNode>): boolean {
        const {pcbLayoutNodes} = this.documentService.snapshot();

        for (const updatedNode of Records.iterate(nodesToUpdate)) {
            const oldRules = pcbLayoutNodes[updatedNode.uid]?.bakedRules;
            const updatedRules = updatedNode.bakedRules;

            const isActiveChange = oldRules?.active !== updatedRules.active;
            if (isActiveChange) {
                return true;
            }

            const isLayerChange = oldRules?.layer !== updatedRules.layer;
            if (isLayerChange) {
                return true;
            }

            const isSmartViaNode = updatedNode.type === PcbNodeTypes.smartVia;
            if (isSmartViaNode) {
                return true;
            }

            const isZoneConnectedLayerChange =
                oldRules &&
                updatedNode.type === PcbNodeTypes.zone &&
                ("connectedLayers" in updatedRules && updatedRules.connectedLayers?.join("-")) !==
                    ("connectedLayers" in oldRules && oldRules.connectedLayers?.join("-"));
            if (isZoneConnectedLayerChange) {
                return true;
            }

            const isHoleTypeChange =
                oldRules &&
                "hole" in updatedRules &&
                updatedRules.hole?.holeType !== ("hole" in oldRules && oldRules.hole?.holeType);
            if (isHoleTypeChange) {
                return true;
            }

            const isEffectiveNetIdChange =
                oldRules &&
                "effectiveNetId" in updatedRules &&
                updatedRules.effectiveNetId !== ("effectiveNetId" in oldRules && oldRules.effectiveNetId);
            if (isEffectiveNetIdChange) {
                return true;
            }
        }

        return false;
    }

    /**
     * This exists because virtual nodes are only added when a rule is updated,
     * so we also need to remove stale virtual nodes.
     */
    private async cleanUpVirtualNodes(
        virtualNodesGoodsFromRules: IVirtualNodesGoodsFromRules[] = [],
        drc: DrcManager,
        pcbLayoutEngine: PcbLayoutEngine | Remote<PcbLayoutEngine>,
    ) {
        const nodesToRemove = virtualNodesGoodsFromRules.flatMap((good) => good.nodesToRemove);
        if (nodesToRemove.length) {
            await this.removeNodes(nodesToRemove, drc, pcbLayoutEngine);
        }
        return nodesToRemove.length;
    }

    /**
     * A tedious function to remove and add any auto selector-based rules for
     * virtual nodes.
     *
     * NOTE: we assume here that the rule set IDs are the same as the IDs of the
     * nodes they apply to. See createPadBasedRulesAndMountingPads and generateVirtualNodesFromRules.
     */
    private updateRuleSets(goodsArray: IVirtualNodesGoodsFromRules[], drc: DrcManager) {
        const ruleSetsToRemove = chain(goodsArray)
            .map((goods) => goods.ruleSetsToRemove)
            .reduce((merged, ruleSetsToRemove) => assign(merged, ruleSetsToRemove))
            .toArray()
            .value();

        const ruleSetsToAdd = chain(goodsArray)
            .map((goods) => goods.ruleSetsToAdd)
            .reduce((merged, ruleSetsToAdd) => assign(merged, ruleSetsToAdd))
            .toArray()
            .value();

        this.documentServiceCommandBatch.push({
            type: CommandType.removePcbRuleSetsCommand,
            ruleSetUids: ruleSetsToRemove,
        });

        this.documentServiceCommandBatch.push({
            type: CommandType.addPcbRuleSetsCommand,
            ruleSets: ruleSetsToAdd,
        });

        drc.recordRuleSetsChange("remove", ruleSetsToRemove);
        drc.recordRuleSetsChange("add", ruleSetsToAdd);
    }

    private async updateVirtualNodes(
        virtualFillNodes: IFillNodesGoods | undefined,
        drc: DrcManager,
        pcbLayoutEngine: PcbLayoutEngine | Remote<PcbLayoutEngine>,
    ) {
        // NOTE: order shouldn't make a difference -- the add/remove/update sets
        // are mutually exclusive.
        // TODO: Also make sure to remove child nodes
        // TODO: [NodeType] - Remove this as cast when we polish the typing for virtualFillNodes
        if (virtualFillNodes) {
            const removeMap: Map<NodeUid, AnyPcbBakedNode> = new Map();

            for (const node of virtualFillNodes.nodesToRemove) {
                removeMap.set(node.uid, node as AnyPcbBakedNode);
            }

            // We have an issue in fill generation where, if a zone is updated resulting in a fill-connected-terminal
            // being covered by the zone, then all stitch vias on that fill should be removed. For some reason, in that
            // case, the stitch vias (to be removed) are not present in the tree manager's record of nodes, when fill
            // generation is run. Since stitch vias are not present, the fill generation logic has no way of returning
            // a list of stitch vias to be removed. To work around this, we "sweep" all virutal stitch vias of fills and
            // allow any required stitch vias to be added via `nodesToAdd`.
            // Importantly, we're still allowing any node data in `nodesToRemove` to be accounted for in this update, too.
            for (const node of Records.iterate(this.documentService.snapshot().pcbLayoutNodes)) {
                if (!PcbNodeHelper.isVirtualNode(node)) {
                    continue;
                }

                const nodeParent = this.documentService.snapshot().pcbLayoutNodes[node.parentUid];
                if (!(nodeParent?.type === PcbNodeTypes.fill && node.type === PcbNodeTypes.via)) {
                    continue;
                }

                removeMap.set(node.uid, node);
            }

            await this.removeNodes(Array.from(removeMap.values()), drc, pcbLayoutEngine);
            this.addNodes(virtualFillNodes.nodesToAdd as AnyPcbBakedNode[], drc, false);
            this.updateNodes(virtualFillNodes.nodesToUpdate as AnyPcbBakedNode[], drc);
        }
    }

    private setShapesMap(virtualFillNodes: IFillNodesGoods | undefined) {
        // NOTE: call `setShapesMap` first because some downstream modules such as
        // DRC subscribes to changes to baked nodes, and by then we want to make sure
        // `shapesMap` is up-to-date in order to calculate connectivity graph correctly
        if (virtualFillNodes) {
            const fills = virtualFillNodes.shapesMap;
            this.pcbPrimitiveStore.setFills(fills);
        }
    }

    private async updateSmartViaConfigsCache(pcbLayoutEngine: PcbLayoutEngine | Remote<PcbLayoutEngine>) {
        const smartViaConfigsCache = usePcbEditorUiStore.getState().smartViaConfigsCache ?? {};
        const updatedCache = await pcbLayoutEngine.updateSmartViaConfigsCache(smartViaConfigsCache);
        usePcbEditorUiStore.getState().setSmartViaConfigsCache(updatedCache);
    }

    private createDrcManager() {
        const finishDrcCheckMap: Record<string, ReturnType<IProblemsStore["runDrcCheck"]>> = {};

        const onValidationStart = (validatorKey: string) => {
            finishDrcCheckMap[validatorKey] = useProblemsStore.getState().runDrcCheck(validatorKey);
        };

        const onValidationFinish = (validatorKey: string, problems: IProblem[], errors: string | false) => {
            const finishDrcCheck = finishDrcCheckMap[validatorKey];
            if (finishDrcCheck) {
                finishDrcCheck(problems, errors);
                delete finishDrcCheckMap[validatorKey];
            } else {
                this.logger.warn(`No DRC check found to finish for validator key: ${validatorKey}`);
            }
        };

        const featureFlagEnabledValidators = this.featureFlagsStore.getState().drcEnabledCheckers;

        const validatorOverrides: Partial<Record<keyof DrcEnabledCheckers, IDrcValidator>> = {
            OverlappingCopperValidator: new OverlappingCopperValidator(workerRunCopperOverlapDrc),
            AirwiresValidator: new AirwiresValidator(workerRunComputeAirwires),
        };

        return new DrcManager(
            this.pcbPrimitiveStore,
            this.pcbConnectivityGraph,
            this.pcbTracingGraph,
            this.documentService,
            this.logger,
            featureFlagEnabledValidators,
            validatorOverrides,
            {
                getDocumentData: () => this.documentStore.getState().document,
            },
            {
                onValidationStart,
                onValidationFinish,
            },
        );
    }

    /**
     * NOTE: This will reuse the nodes inside the existing web worker instead of
     * copying documentState and treeNodes again.
     */
    private async getPcbLayoutEngine(
        documentState: PcbDocumentState,
        treeNodes: PcbNodesMap<AnyPcbBakedNode | AnyPcbNode>,
        pcbLayoutRules: PcbRuleSetsMap,
    ) {
        const featureFlags = chain(this.featureFlagsStore.getState())
            .pickBy((value) => {
                // State for useFeatureFlagStore(..) contains an unsubscribe function - we can't send a function over
                // the thread boundary, so functions are filtered
                return typeof value !== "function";
            })
            .value() as FeatureFlagValues;

        if (areWeTestingWithJest() || (featureFlags.disablePcbLayoutEngineWorker ?? false)) {
            return new PcbLayoutEngine(documentState, treeNodes, pcbLayoutRules, FluxLogger, featureFlags);
        }
        // IDEA: rely on PcbLayoutEngineInWorkerMock here instead for jest?
        return await createPcbLayoutEngineWorker(documentState, featureFlags);
    }
}
